import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import { useSEO } from "../hooks/useSEO";
import { Button } from "../components/ui/button";
import api, { formatApiErrorDetail } from "../lib/api";
import { BookingStepper } from "../booking/BookingStepper";
import { ServiceStep } from "../booking/ServiceStep";
import { DateStep } from "../booking/DateStep";
import { TimeStep } from "../booking/TimeStep";
import { InfoStep } from "../booking/InfoStep";
import { SummaryStep } from "../booking/SummaryStep";
import { SuccessStep } from "../booking/SuccessStep";

function toDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function buildBookingPayload({ service, date, time, formData, consentAccepted }) {
  return {
    service_id: service.id,
    date: toDateStr(date),
    start_time: time,
    customer_name: formData.customer_name,
    customer_email: formData.customer_email,
    customer_phone: formData.customer_phone,
    notes: formData.notes,
    consent_accepted: consentAccepted,
  };
}

const emptyForm = { customer_name: "", customer_email: "", customer_phone: "", notes: "" };

export default function Book() {
  const { t, lang } = useLanguage();
  useSEO(
    lang === "nl"
      ? "Afspraak maken | EliteTheBarber Kapper Utrecht"
      : "Book appointment | EliteTheBarber Barber Utrecht",
    lang === "nl"
      ? "Maak snel en eenvoudig online een afspraak bij kapper EliteTheBarber in Utrecht. Kies je dienst, datum en tijd — direct bevestigd."
      : "Quickly and easily book an appointment with barber EliteTheBarber in Utrecht. Pick your service, date and time — instantly confirmed."
  );
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [service, setService] = useState(null);
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [infoError, setInfoError] = useState("");
  const [summaryError, setSummaryError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    const serviceId = searchParams.get("service");
    if (serviceId) {
      api.get("/services").then(({ data }) => {
        const found = data.find((s) => s.id === serviceId);
        if (found) {
          setService(found);
          setStep(2);
        }
      }).catch(() => {});
    }
  }, [searchParams]);

  const reset = () => {
    setStep(1); setService(null); setDate(undefined); setTime(null);
    setFormData(emptyForm); setConsentAccepted(false); setBooking(null);
    setInfoError(""); setSummaryError("");
  };

  const goNextFromInfo = () => {
    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone) {
      setInfoError(t("booking.errors.invalidData"));
      return;
    }
    if (!consentAccepted) {
      setInfoError(t("booking.consent.required"));
      return;
    }
    setInfoError("");
    setStep(5);
  };

  const confirmBooking = async () => {
    setSubmitting(true);
    setSummaryError("");
    try {
      const { data } = await api.post("/bookings", buildBookingPayload({ service, date, time, formData, consentAccepted }));
      setBooking(data);
      setStep(6);
    } catch (e) {
      setSummaryError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="book-page" className="py-16 sm:py-20 px-4 sm:px-6 min-h-[70vh]">
      <div className="max-w-3xl mx-auto">
        {step < 6 && (
          <>
            <h1 className="font-serif text-3xl sm:text-4xl text-white mb-10 text-center tracking-tight">{t("booking.title")}</h1>
            <BookingStepper currentStep={step} />
          </>
        )}

        {step === 1 && (
          <ServiceStep selectedServiceId={service?.id} onSelect={(s) => { setService(s); setStep(2); }} />
        )}

        {step === 2 && (
          <>
            <DateStep selectedDate={date} onSelect={(d) => { setDate(d); setTime(null); setStep(3); }} />
            <NavButtons onBack={() => setStep(1)} />
          </>
        )}

        {step === 3 && (
          <>
            <TimeStep serviceId={service.id} date={date} selectedTime={time} onSelect={(t2) => { setTime(t2); setStep(4); }} />
            <NavButtons onBack={() => setStep(2)} />
          </>
        )}

        {step === 4 && (
          <>
            <InfoStep formData={formData} onChange={setFormData} consentAccepted={consentAccepted} onConsentChange={setConsentAccepted} error={infoError} />
            <NavButtons onBack={() => setStep(3)} onNext={goNextFromInfo} nextLabel={t("common.next")} />
          </>
        )}

        {step === 5 && (
          <>
            <SummaryStep service={service} date={date} time={time} formData={formData} onConfirm={confirmBooking} submitting={submitting} error={summaryError} />
            <NavButtons onBack={() => setStep(4)} />
          </>
        )}

        {step === 6 && <SuccessStep booking={booking} onNewBooking={reset} />}
      </div>
    </div>
  );
}

const NavButtons = ({ onBack, onNext, nextLabel }) => {
  const { t } = useLanguage();
  return (
    <div className="flex items-center gap-4 mt-8">
      <Button data-testid="booking-back-button" onClick={onBack} variant="outline" className="bg-transparent border border-white/20 text-white hover:border-gold hover:text-gold rounded-none px-6">
        {t("common.back")}
      </Button>
      {onNext && (
        <Button data-testid="booking-next-button" onClick={onNext} className="bg-gold text-white hover:bg-gold-hover rounded-none px-6">
          {nextLabel}
        </Button>
      )}
    </div>
  );
};
